import { useState, useEffect, useCallback } from 'react';
import {
  Export, ArrowsClockwise, CheckCircle, XCircle, CircleNotch, ArrowRight,
  PlugsConnected, Download, Upload, MagnifyingGlass, Gear, Lightning,
  WebhooksLogo, PaperPlaneTilt, Check, Warning, Info
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchStats, setMatchStats] = useState(null);
  const [config, setConfig] = useState({ client_id: '', client_secret: '', redirect_uri: '', division: '305' });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [matchFilter, setMatchFilter] = useState('');
  // Zapier state
  const [zapierUrl, setZapierUrl] = useState('');
  const [zapierSaving, setZapierSaving] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState(new Set());
  const [zapierExporting, setZapierExporting] = useState(false);
  const [zapierResult, setZapierResult] = useState(null);
  const [facilitySearch, setFacilitySearch] = useState('');

  const fetchStatus = useCallback(() => {
    fetch(`${API}/exact/status`, { credentials: 'include' })
      .then(r => r.json())
      .then(setStatus)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const saveConfig = async () => {
    setSaving(true);
    await fetch(`${API}/exact/config`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    });
    setSaving(false);
    fetchStatus();
  };

  const startAuth = async () => {
    const res = await fetch(`${API}/exact/auth-url`, { credentials: 'include' });
    const data = await res.json();
    if (data.auth_url) window.location.href = data.auth_url;
  };

  const importFromExact = async () => {
    setImporting(true);
    try {
      const res = await fetch(`${API}/exact/import`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.accounts !== undefined) {
        fetchAccounts();
        fetchStatus();
      }
    } catch { /* ignore */ }
    setImporting(false);
  };

  const fetchAccounts = () => {
    fetch(`${API}/exact/accounts?limit=200`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setAccounts(d.accounts || []));
  };

  const runMatching = async () => {
    setMatching(true);
    try {
      const res = await fetch(`${API}/exact/match`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      setMatchStats(data);
      fetchMatches();
    } catch { /* ignore */ }
    setMatching(false);
  };

  const fetchMatches = useCallback(() => {
    const params = matchFilter ? `?match_type=${matchFilter}&limit=200` : '?limit=200';
    fetch(`${API}/exact/matches${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setMatches(d.matches || []));
  }, [matchFilter]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const pushToExact = async (facilityIds, mode) => {
    setPushing(true);
    setPushResult(null);
    try {
      const res = await fetch(`${API}/exact/push`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility_ids: facilityIds, mode }),
      });
      const data = await res.json();
      setPushResult(data);
      fetchStatus();
    } catch { /* ignore */ }
    setPushing(false);
  };

  // Zapier functions
  const fetchZapierConfig = useCallback(() => {
    fetch(`${API}/zapier/config`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setZapierUrl(d.webhook_url || ''));
  }, []);

  const fetchFacilities = useCallback(() => {
    const params = facilitySearch ? `?search=${encodeURIComponent(facilitySearch)}&limit=100` : '?limit=100';
    fetch(`${API}/facilities${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setFacilities(d.facilities || []));
  }, [facilitySearch]);

  useEffect(() => { fetchZapierConfig(); }, [fetchZapierConfig]);
  useEffect(() => { fetchFacilities(); }, [fetchFacilities]);

  const saveZapierConfig = async () => {
    setZapierSaving(true);
    await fetch(`${API}/zapier/config`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ webhook_url: zapierUrl }),
    });
    setZapierSaving(false);
  };

  const toggleFacility = (fid) => {
    setSelectedFacilities(prev => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid);
      else next.add(fid);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedFacilities.size === facilities.length) {
      setSelectedFacilities(new Set());
    } else {
      setSelectedFacilities(new Set(facilities.map(f => f.facility_id)));
    }
  };

  const exportToZapier = async () => {
    if (!zapierUrl || selectedFacilities.size === 0) return;
    setZapierExporting(true);
    setZapierResult(null);
    try {
      const res = await fetch(`${API}/zapier/export`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webhook_url: zapierUrl,
          facility_ids: Array.from(selectedFacilities),
        }),
      });
      const data = await res.json();
      setZapierResult(data);
    } catch (err) {
      setZapierResult({ success: 0, errors: selectedFacilities.size, results: [] });
    }
    setZapierExporting(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isConnected = status?.connected;
  const isMock = status?.mock_mode;

  return (
    <div className="animate-fade-in" data-testid="exports-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight">Export & Sync</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Bi-directionele sync met Exact Online en webhook export naar Zapier
        </p>
      </div>

      {/* Connection Status */}
      <div className={`border ${isConnected ? 'border-emerald-200 bg-emerald-50' : 'border-zinc-200 bg-white'} p-5 mb-6`} data-testid="exact-status">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlugsConnected size={24} weight="bold" className={isConnected ? 'text-emerald-600' : 'text-zinc-400'} />
            <div>
              <div className="text-sm font-medium">
                {isConnected ? 'Verbonden met Exact Online' : 'Niet verbonden'}
                {isMock && <span className="ml-2 text-xs font-normal text-amber-600">(Demo modus)</span>}
              </div>
              {isConnected && status?.connected_at && (
                <div className="text-xs text-zinc-500">Verbonden: {new Date(status.connected_at).toLocaleString()}</div>
              )}
              {status?.division && <div className="text-xs text-zinc-400">Divisie: {status.division}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isMock && (
              <Badge className="rounded-full px-3 py-1 text-xs font-data bg-amber-100 text-amber-700" data-testid="mock-badge">
                DEMO
              </Badge>
            )}
            <Badge className={`rounded-full px-3 py-1 text-xs font-data ${isConnected ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100 text-zinc-600'}`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue={isConnected ? "sync" : "setup"}>
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          {['setup', 'sync', 'matches', 'push', 'zapier'].map(tab => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm capitalize"
            >
              {tab === 'setup' ? 'Configuratie' : tab === 'sync' ? 'Importeren' : tab === 'matches' ? 'Matching' : tab === 'push' ? 'Push naar Exact' : 'Zapier'}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Setup Tab */}
        <TabsContent value="setup" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Gear size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Exact Online Configuratie</h3>
            </div>
            {isMock && (
              <div className="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 text-sm text-amber-800">
                <Info size={16} className="mt-0.5 shrink-0" />
                <div>
                  <strong>Demo modus actief.</strong> Geen Exact Online credentials geconfigureerd.
                  De app gebruikt gesimuleerde data zodat je de volledige import/match/push flow kunt testen.
                  Configureer echte credentials hieronder om met je Exact omgeving te verbinden.
                </div>
              </div>
            )}
            <p className="text-sm text-zinc-500 mb-4">
              Maak een app aan in het <a href="https://apps.exactonline.com" target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">Exact Online App Center</a> en vul hieronder de credentials in.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Client ID</label>
                <Input data-testid="exact-client-id" value={config.client_id} onChange={e => setConfig(c => ({...c, client_id: e.target.value}))} placeholder="Exact Client ID" className="rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Client Secret</label>
                <Input data-testid="exact-client-secret" type="password" value={config.client_secret} onChange={e => setConfig(c => ({...c, client_secret: e.target.value}))} placeholder="Exact Client Secret" className="rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Redirect URI</label>
                <Input data-testid="exact-redirect-uri" value={config.redirect_uri} onChange={e => setConfig(c => ({...c, redirect_uri: e.target.value}))} placeholder="https://...preview.emergentagent.com/api/exact/callback" className="rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 mb-1 block">Divisie</label>
                <Input data-testid="exact-division" value={config.division} onChange={e => setConfig(c => ({...c, division: e.target.value}))} placeholder="305" className="rounded-sm text-sm" />
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="save-config-btn" onClick={saveConfig} disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors">
                {saving ? 'Opslaan...' : 'Configuratie opslaan'}
              </button>
              {status?.client_id_set && (
                <button data-testid="connect-exact-btn" onClick={startAuth}
                  className="px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors flex items-center gap-2">
                  <PlugsConnected size={14} /> Verbind met Exact Online
                </button>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Import/Sync Tab */}
        <TabsContent value="sync" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Download size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Importeer uit Exact Online</h3>
              {isMock && <Badge className="rounded-full px-2 py-0.5 text-[10px] bg-amber-100 text-amber-700">DEMO DATA</Badge>}
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Haal alle klanten (accounts) en contactpersonen op uit Exact Online divisie {status?.division || '305'}.
              {isMock && ' In demo modus worden gesimuleerde accounts geladen.'}
            </p>
            <button data-testid="import-exact-btn" onClick={importFromExact} disabled={importing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {importing ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Download size={14} />}
              {importing ? 'Importeren...' : 'Start Import'}
            </button>
            {status?.last_import && (
              <div className="mt-3 text-xs text-zinc-500">
                Laatste import: {new Date(status.last_import).toLocaleString()} — {status.accounts_imported || 0} accounts, {status.contacts_imported || 0} contacten
              </div>
            )}
          </div>

          {/* Imported Accounts */}
          <div className="border border-zinc-200 overflow-hidden" data-testid="exact-accounts-table">
            <div className="bg-white px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
              <h4 className="font-heading text-sm font-semibold">Geimporteerde Exact Accounts ({accounts.length})</h4>
              {accounts.length === 0 && <button onClick={fetchAccounts} className="text-xs text-[#0055FF] hover:underline">Laden</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Naam</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Stad</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Land</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Telefoon</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Email</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Website</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400 text-xs">Geen accounts. Klik &quot;Start Import&quot; hierboven.</td></tr>
                  ) : accounts.map(a => (
                    <tr key={a.exact_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 font-medium">{a.name}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{a.city}</td>
                      <td className="px-4 py-2.5 text-zinc-500 font-data text-xs">{a.country}</td>
                      <td className="px-4 py-2.5 font-data text-xs">{a.phone || '-'}</td>
                      <td className="px-4 py-2.5 font-data text-xs">{a.email || '-'}</td>
                      <td className="px-4 py-2.5 font-data text-xs text-[#0055FF]">{a.website ? a.website.replace(/^https?:\/\//, '').slice(0, 30) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <MagnifyingGlass size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Matching Engine</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Match Exact klanten met gevonden zwembaden op basis van naam, adres, domein en telefoon.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <button data-testid="run-matching-btn" onClick={runMatching} disabled={matching}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {matching ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Lightning size={14} weight="bold" />}
                {matching ? 'Matchen...' : 'Start Matching'}
              </button>
            </div>
            {matchStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
                {[
                  { l: 'Totaal', v: matchStats.total, c: 'text-zinc-700' },
                  { l: 'Exact Match', v: matchStats.exact_matches, c: 'text-emerald-600' },
                  { l: 'Hoge Match', v: matchStats.high_matches, c: 'text-blue-600' },
                  { l: 'Gedeeltelijk', v: matchStats.partial_matches, c: 'text-amber-600' },
                  { l: 'Geen Match', v: matchStats.no_match, c: 'text-zinc-400' },
                ].map((s, i) => (
                  <div key={i} className="bg-white p-3 text-center">
                    <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-3">
            {['', 'exact', 'high', 'partial', 'none'].map(f => (
              <button key={f} onClick={() => setMatchFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${matchFilter === f ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-zinc-600 border-zinc-200'}`}>
                {f === '' ? 'Alle' : f === 'exact' ? 'Exact' : f === 'high' ? 'Hoog' : f === 'partial' ? 'Gedeeltelijk' : 'Geen'}
              </button>
            ))}
          </div>

          <div className="border border-zinc-200 overflow-hidden" data-testid="matches-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Exact Account</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Score</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Match Type</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">PoolFIX Facility</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Lead Score</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 text-xs">Geen matches. Klik &quot;Start Matching&quot;.</td></tr>
                  ) : matches.map((m, i) => (
                    <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{m.exact_account?.name}</div>
                        <div className="text-xs text-zinc-400">{m.exact_account?.city}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`font-data text-sm font-bold ${m.match_score >= 80 ? 'text-emerald-600' : m.match_score >= 60 ? 'text-blue-600' : m.match_score >= 40 ? 'text-amber-600' : 'text-zinc-400'}`}>
                          {m.match_score}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge className={`rounded-full px-2 py-0.5 text-xs font-data ${
                          m.match_type === 'exact' ? 'bg-emerald-100 text-emerald-800' :
                          m.match_type === 'high' ? 'bg-blue-100 text-blue-800' :
                          m.match_type === 'partial' ? 'bg-amber-100 text-amber-800' :
                          'bg-zinc-100 text-zinc-500'
                        }`}>{m.match_type}</Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        {m.match ? (
                          <div>
                            <div className="font-medium text-[#0055FF]">{m.match.name}</div>
                            <div className="text-xs text-zinc-400">{m.match.city}</div>
                          </div>
                        ) : <span className="text-xs text-zinc-400">-</span>}
                      </td>
                      <td className="px-4 py-2.5 font-data text-sm font-bold">{m.match?.lead_score || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Push Tab */}
        <TabsContent value="push" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <Upload size={20} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-lg font-semibold">Push naar Exact Online</h3>
              {isMock && <Badge className="rounded-full px-2 py-0.5 text-[10px] bg-amber-100 text-amber-700">DEMO</Badge>}
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Stuur verrijkte data terug naar Exact. Update bestaande klanten of maak nieuwe accounts aan.
              {isMock && ' In demo modus worden wijzigingen lokaal gesimuleerd.'}
            </p>
            <div className="flex gap-3 mb-4">
              <button data-testid="push-update-btn" onClick={() => {
                const matched = matches.filter(m => m.match).map(m => m.match.facility_id);
                if (matched.length > 0) pushToExact(matched, 'update');
              }} disabled={pushing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {pushing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Update matched accounts ({matches.filter(m => m.match).length})
              </button>
              <button data-testid="push-create-btn" onClick={() => {
                const unmatched = matches.filter(m => !m.match).map(m => m.exact_account?.exact_id).filter(Boolean);
                if (unmatched.length > 0) {
                  pushToExact(unmatched, 'create');
                }
              }} disabled={pushing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-50 transition-colors">
                <Export size={14} /> Nieuwe accounts ({matches.filter(m => !m.match).length})
              </button>
            </div>
            {status?.last_push && (
              <div className="text-xs text-zinc-500">Laatste push: {new Date(status.last_push).toLocaleString()}</div>
            )}
            {pushResult && (
              <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-sm">
                <div className="font-medium flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600" /> Push resultaat
                </div>
                <div className="text-xs mt-1">Aangemaakt: {pushResult.created} | Bijgewerkt: {pushResult.updated}</div>
                {pushResult.mock_mode && <div className="text-xs text-amber-600 mt-1">Gesimuleerd in demo modus</div>}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Zapier Tab */}
        <TabsContent value="zapier" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <PaperPlaneTilt size={20} weight="bold" className="text-[#FF6B35]" />
              <h3 className="font-heading text-lg font-semibold">Zapier Webhook Export</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">
              Exporteer geselecteerde leads met alle verrijkte data naar een Zapier webhook.
              Zapier kan de data vervolgens doorsluizen naar elk CRM, spreadsheet of tool.
            </p>

            {/* Webhook URL config */}
            <div className="mb-4">
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Webhook URL</label>
              <div className="flex gap-2">
                <Input
                  data-testid="zapier-webhook-url"
                  value={zapierUrl}
                  onChange={e => setZapierUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/hooks/catch/..."
                  className="rounded-sm text-sm flex-1"
                />
                <button data-testid="save-zapier-config-btn" onClick={saveZapierConfig} disabled={zapierSaving}
                  className="px-4 py-2 text-sm font-medium bg-[#0F172A] text-white rounded-sm hover:bg-[#1E293B] disabled:opacity-50 transition-colors whitespace-nowrap">
                  {zapierSaving ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            </div>
          </div>

          {/* Select facilities to export */}
          <div className="border border-zinc-200 bg-white p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-heading text-sm font-semibold">Selecteer leads om te exporteren</h4>
              <div className="flex items-center gap-3">
                <Input
                  data-testid="zapier-facility-search"
                  value={facilitySearch}
                  onChange={e => setFacilitySearch(e.target.value)}
                  placeholder="Zoek op naam of stad..."
                  className="rounded-sm text-sm w-56"
                />
                <button onClick={selectAll} className="text-xs text-[#0055FF] hover:underline whitespace-nowrap" data-testid="select-all-btn">
                  {selectedFacilities.size === facilities.length ? 'Deselecteer alles' : 'Selecteer alles'}
                </button>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto border border-zinc-100 rounded-sm">
              {facilities.length === 0 ? (
                <div className="px-4 py-8 text-center text-zinc-400 text-xs">Geen faciliteiten gevonden.</div>
              ) : facilities.map(f => (
                <label key={f.facility_id}
                  className={`flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${selectedFacilities.has(f.facility_id) ? 'bg-blue-50' : ''}`}
                  data-testid={`zapier-facility-${f.facility_id}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedFacilities.has(f.facility_id)}
                    onChange={() => toggleFacility(f.facility_id)}
                    className="w-4 h-4 rounded border-zinc-300 text-[#0055FF] focus:ring-[#0055FF]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-xs text-zinc-400">{f.city}, {f.country_code} — {f.facility_type}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-data text-xs font-bold ${f.lead_score >= 70 ? 'text-emerald-600' : f.lead_score >= 40 ? 'text-amber-600' : 'text-zinc-400'}`}>
                      Score: {f.lead_score || 0}
                    </span>
                    {f.general_email && <Badge className="rounded-full px-1.5 py-0.5 text-[9px] bg-emerald-100 text-emerald-700">Email</Badge>}
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-zinc-500">{selectedFacilities.size} van {facilities.length} geselecteerd</span>
              <button
                data-testid="export-zapier-btn"
                onClick={exportToZapier}
                disabled={zapierExporting || !zapierUrl || selectedFacilities.size === 0}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-[#FF6B35] text-white rounded-sm hover:bg-[#E55A25] disabled:opacity-50 transition-colors"
              >
                {zapierExporting ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                {zapierExporting ? 'Exporteren...' : `Exporteer ${selectedFacilities.size} leads naar Zapier`}
              </button>
            </div>
          </div>

          {/* Export Results */}
          {zapierResult && (
            <div className={`border p-4 ${zapierResult.errors > 0 && zapierResult.success === 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`} data-testid="zapier-result">
              <div className="flex items-center gap-2 mb-2">
                {zapierResult.success > 0 ? (
                  <CheckCircle size={20} weight="bold" className="text-emerald-600" />
                ) : (
                  <Warning size={20} weight="bold" className="text-red-600" />
                )}
                <span className="font-heading text-sm font-semibold">
                  Export resultaat: {zapierResult.success} verzonden, {zapierResult.errors} fouten
                </span>
              </div>
              {zapierResult.results && zapierResult.results.length > 0 && (
                <div className="mt-2 space-y-1">
                  {zapierResult.results.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {r.status === 'success' ? (
                        <Check size={12} className="text-emerald-600" />
                      ) : (
                        <XCircle size={12} className="text-red-500" />
                      )}
                      <span className="font-medium">{r.name || r.facility_id}</span>
                      {r.status !== 'success' && <span className="text-red-500">{r.reason}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
